import { zodResolver } from '@hookform/resolvers/zod'
import { resetPasswordSchema } from '@repo/shared'
import { Button, Input } from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, KeyRound } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import useResetPassword from '../../lib/api/auth/reset-password'

import type { ResetPasswordSchema } from '@repo/shared'

import Logo from '@/assets/logo.svg?react'
import LanguageSettings from '@/components/layout/header/language-settings'
import FormControl from '@/components/shared/form-control'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/(auth)/reset-password')({
  head: () => ({ meta: [{ title: localizedPageTitle('resetPassword') }] }),
  component: ResetPasswordPage,
  validateSearch: searchSchema,
})

function ResetPasswordPage() {
  useDocumentTitle('resetPassword')
  const { t } = useTranslation(['resetPassword', 'login'])
  const { token } = Route.useSearch()

  const { mutate, isPending, errorMessage } = useResetPassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: token ?? '',
    },
  })

  const onSubmit = (data: ResetPasswordSchema) => {
    mutate(data)
  }

  return (
    <div className="flex min-h-dvh">
      <div className="bg-card flex min-h-dvh w-full flex-col justify-between border-r md:max-w-[500px]">
        <div className="border-border flex items-center gap-3 border-b md:gap-6">
          <div className="border-r p-4 md:p-6">
            <h1 className="text-base font-semibold md:text-lg">
              {t('resetPassword:title')}
            </h1>
          </div>
          <Logo className="h-6 shrink-0 md:h-auto" />
          <div className="ml-auto [&>div]:border-r-0">
            <LanguageSettings />
          </div>
        </div>

        <div className="mx-auto mt-6 w-full px-4 md:px-6">
          {!token ? (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold">
                {t('resetPassword:linkExpiredTitle')}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t('resetPassword:errors.missingToken')}
              </p>
              <Link
                to="/forgot-password"
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
              >
                {t('resetPassword:requestNewLink')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <p className="text-muted-foreground text-sm">
                {t('resetPassword:description')}
              </p>

              <input type="hidden" {...register('token')} />

              <FormControl
                htmlFor="password"
                labelText={t('resetPassword:passwordLabel')}
                error={errors.password?.message}
              >
                <Input
                  id="password"
                  {...register('password')}
                  type="password"
                  placeholder="••••••••"
                  className="bg-background"
                />
              </FormControl>

              <FormControl
                htmlFor="confirmPassword"
                labelText={t('resetPassword:confirmPasswordLabel')}
                error={errors.confirmPassword?.message}
              >
                <Input
                  id="confirmPassword"
                  {...register('confirmPassword')}
                  type="password"
                  placeholder="••••••••"
                  className="bg-background"
                />
              </FormControl>

              <Button
                disabled={isPending}
                isLoading={isPending}
                type="submit"
                variant="secondary"
                size="xl"
                className="border-border primary-bg-gradient w-full border"
              >
                <KeyRound className="size-4" />
                {t('resetPassword:submitButton')}
              </Button>

              {errorMessage && (
                <p className="text-destructive text-center text-sm">
                  {errorMessage}
                </p>
              )}

              <div className="flex justify-center">
                <Link
                  to="/login"
                  className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                >
                  <ArrowLeft className="size-4" />
                  {t('resetPassword:backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>

        <p className="text-muted-foreground mt-auto px-4 pb-4 text-xs md:px-6 md:pb-6">
          {t('login:allRightsReserved')}
        </p>

        <div className="border-t p-4 md:p-6">
          <h2 className="text-lg font-bold md:text-xl">
            {t('login:welcomeToGroute')}
          </h2>
        </div>
      </div>

      <div className="hidden flex-1 p-4 md:flex">
        <div className="relative hidden flex-1 overflow-hidden md:flex md:items-center md:justify-center">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full rounded-2xl object-cover"
          >
            <source src="/animated-gradient.mp4" type="video/mp4" />
          </video>
        </div>
      </div>
    </div>
  )
}
